import { z } from 'zod';

// admin-ticket-analytics-dashboard S1 (R1-R4) — Live per-location and org-wide
// KPI aggregate contracts. Shared Zod schemas — single source of truth for
// backend serialization and frontend deserialization. Mirrors audit-schemas.ts
// conventions (query schema + response schema(s) in one file).

export const AnalyticsRangeQuerySchema = z
  .object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
  })
  .refine((v) => !v.from || !v.to || new Date(v.from) <= new Date(v.to), {
    message: "'to' must not precede 'from'",
    path: ['to'],
  });
export type AnalyticsRangeQuery = z.infer<typeof AnalyticsRangeQuerySchema>;

// Core KPI shape (R1) — shared between the per-location response and each row
// of the org-wide per-location breakdown table (R2).
export const AnalyticsKpiSchema = z.object({
  volume: z.number().int().nonnegative(),
  // amountPaidCents - tipAmountCents (tips excluded, JQ4-14 convention).
  netRevenueCents: z.number().int(),
  avgRetrievalSeconds: z.number().int().nonnegative().nullable(),
  paymentStatusBreakdown: z.record(z.string(), z.number().int().nonnegative()),
  statusBreakdown: z.record(z.string(), z.number().int().nonnegative()),
  ticketTypeBreakdown: z.object({
    sms: z.number().int().nonnegative(),
    qr: z.number().int().nonnegative(),
  }),
});
export type AnalyticsKpi = z.infer<typeof AnalyticsKpiSchema>;

export const LocationAnalyticsResponseSchema = AnalyticsKpiSchema.extend({
  from: z.string(), // ISO 8601 UTC
  to: z.string(), // ISO 8601 UTC
});
export type LocationAnalyticsResponse = z.infer<typeof LocationAnalyticsResponseSchema>;

export const LocationAnalyticsBreakdownRowSchema = AnalyticsKpiSchema.extend({
  locationId: z.string().uuid(),
});
export type LocationAnalyticsBreakdownRow = z.infer<typeof LocationAnalyticsBreakdownRowSchema>;

export const OrgAnalyticsResponseSchema = z.object({
  total: AnalyticsKpiSchema,
  byLocation: z.array(LocationAnalyticsBreakdownRowSchema),
  from: z.string(),
  to: z.string(),
});
export type OrgAnalyticsResponse = z.infer<typeof OrgAnalyticsResponseSchema>;
